import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import "../style/Studentdatapage.css";
import { PageHeader, SearchInput, EmptyState } from "../components/admin/AdminUI";
import axios from "axios";
import Swal from "sweetalert2";
import SyncLoader from "react-spinners/SyncLoader";
import * as XLSX from "xlsx";
import { formatDmy, fileDateStamp } from "../utils/dateFormat";

const CLASS_ORDER = ["OT", "NT", "BD", "BLC", "BCMF", "MASTERCLASS", "HH", "CBH", "CBD", "BWG", "MMM", "CONFERENCE"];

const scholarshipLabel = { "need-based": "Opportunity Scholarship", merit: "Leadership Scholarship" };

// Rincian total diskon (tooltip badge + kolom Excel), mis.
// "CEA 25% · PIC 75% · Student pays 0%".
const discountBreakdownText = (split) =>
  [
    `CEA ${split.cea}%`,
    split.pic ? `PIC ${split.pic}%` : null,
    split.other ? `Other payer ${split.other}%` : null,
    `Student pays ${split.student}%`,
  ]
    .filter(Boolean)
    .join(" · ");

// Student ID (mis. 22100044) diurutkan per 2 digit depan (tahun) lalu 4 digit
// belakang (nomor urut); 2 digit tengah diabaikan. Sama dengan urutan di
// Attendance Grid (getAttendanceGrid di backend).
const compareStudentId = (a, b) => {
  const idA = String(a || "");
  const idB = String(b || "");
  const byYear = (parseInt(idA.slice(0, 2), 10) || 0) - (parseInt(idB.slice(0, 2), 10) || 0);
  if (byYear !== 0) return byYear;
  const bySeq = (parseInt(idA.slice(-4), 10) || 0) - (parseInt(idB.slice(-4), 10) || 0);
  if (bySeq !== 0) return bySeq;
  return idA.localeCompare(idB);
};

const getClassBatches = (classList) => {
  try {
    const cl = typeof classList === "string" ? JSON.parse(classList) : classList;
    return [...new Set((cl?.className || []).map((c) => c.batch).filter(Boolean))].sort(
      (a, b) => Number(a) - Number(b),
    );
  } catch {
    return [];
  }
};

const deriveFilterOptions = (data) => {
  const hasNoBatch = data.some((i) => getClassBatches(i.classList).length === 0);
  const batches = [
    ...[...new Set(data.flatMap((i) => getClassBatches(i.classList)))]
      .sort((a, b) => Number(a) - Number(b))
      .map((b) => ({ value: b, label: `Batch ${b}` })),
    ...(hasNoBatch ? [{ value: "-", label: "(No batch)" }] : []),
  ];
  const roles = [...new Set(data.map((i) => i.role || "-"))]
    .sort()
    .map((r) => ({ value: r, label: r === "-" ? "(No role)" : r }));
  const classes = [...new Set(
    data.flatMap((i) => {
      try {
        const cl = typeof i.classList === "string" ? JSON.parse(i.classList) : i.classList;
        return (cl?.className || []).map((c) => c.name);
      } catch { return []; }
    }),
  )]
    .sort((a, b) => CLASS_ORDER.indexOf(a) - CLASS_ORDER.indexOf(b))
    .map((c) => ({ value: c, label: c }));
  const programs = [...new Set(data.map((i) => i.program).filter(Boolean))]
    .sort()
    .map((p) => ({ value: p, label: p }));
  const classTypes = [...new Set(data.map((i) => i.classType || "-"))]
    .sort()
    .map((c) => ({ value: c, label: c === "-" ? "(Not set)" : c }));
  const scholarships = [...new Set(data.map((i) => i.scholarshipType || "none"))]
    .sort()
    .map((s) => ({ value: s, label: s === "none" ? "Non-Scholarship" : scholarshipLabel[s] || s }));
  const paymentPlans = [...new Set(data.map((i) => i.paymentPlan || "full"))]
    .sort()
    .map((p) => ({ value: p, label: p === "installment" ? "Installment" : "Full Payment" }));
  return { batches, roles, classes, programs, classTypes, scholarships, paymentPlans };
};

const FILTER_GROUPS = [
  { key: "batches", title: "Batch" },
  { key: "roles", title: "Role" },
  { key: "classes", title: "Kelas" },
  { key: "programs", title: "Program" },
  { key: "classTypes", title: "Class Type" },
  { key: "scholarships", title: "Scholarship" },
  { key: "paymentPlans", title: "Payment Plan" },
];

const INITIAL_FILTER = {
  batches: [], roles: [], classes: [], programs: [], classTypes: [],
  scholarships: [], paymentPlans: [], midBatch: false, cuti: false, specialGuest: false,
  earlyRegis: false, ceaToChurch: false,
};

const isOnLeave = (onLeave, currentBatch, currentClass) => {
  if (!onLeave || !currentClass) return false;
  const leave = typeof onLeave === "string" ? JSON.parse(onLeave) : onLeave;
  if (!leave || !leave.classes) return false;
  return leave.classes.some((c) => c.name === currentClass && c.batch === currentBatch);
};

const getClassScheduleList = (classList) => {
  try {
    const cl = typeof classList === "string" ? JSON.parse(classList) : classList;
    return [...(cl?.className || [])].sort(
      (a, b) => CLASS_ORDER.indexOf(a.name) - CLASS_ORDER.indexOf(b.name),
    );
  } catch {
    return [];
  }
};

const getMidBatchInfo = (classList, program, currentBatch) => {
  if (program !== "1 Year Program") return null;
  try {
    const cl = typeof classList === "string" ? JSON.parse(classList) : classList;
    const classes = cl?.className || [];
    const batchMap = {};
    classes.forEach((c) => {
      if (!c.batch) return;
      if (!batchMap[c.batch]) batchMap[c.batch] = [];
      batchMap[c.batch].push(c.name);
    });
    const batches = Object.keys(batchMap).sort((a, b) => Number(a) - Number(b));
    if (batches.length <= 1) return null;
    const allNames = Object.values(batchMap).flat();
    if (allNames.length !== new Set(allNames).size) return null;
    const activeBatches = batches.filter((b) => Number(b) >= Number(currentBatch));
    if (activeBatches.length === 0) return null;
    return activeBatches.map((batch) => {
      const sorted = batchMap[batch].sort((a, b) => CLASS_ORDER.indexOf(a) - CLASS_ORDER.indexOf(b));
      return { batch, first: sorted[0], last: sorted[sorted.length - 1] };
    });
  } catch {
    return null;
  }
};

const matchesFilter = (item, sel, currentBatch, currentClass) => {
  const inGroup = (selected, value) => selected.length === 0 || selected.includes(value);
  const itemBatches = getClassBatches(item.classList);

  let classEntries = [];
  try {
    const cl = typeof item.classList === "string" ? JSON.parse(item.classList) : item.classList;
    classEntries = cl?.className || [];
  } catch { classEntries = []; }

  const batchSel = sel.batches.length > 0;
  const classSel = sel.classes.length > 0;

  // Kalau batch DAN kelas sama-sama dipilih, keduanya HARUS cocok pada ENTRY
  // kelas yang sama (mis. "MMM" DI "batch 4") — bukan dicek terpisah. Kalau
  // hanya salah satu yang dipilih, perilaku lama tetap.
  let batchClassOk;
  if (batchSel && classSel) {
    batchClassOk = classEntries.some(
      (c) =>
        sel.classes.includes(c.name) &&
        (c.batch ? sel.batches.includes(c.batch) : sel.batches.includes("-")),
    );
  } else if (batchSel) {
    batchClassOk = itemBatches.length
      ? itemBatches.some((b) => sel.batches.includes(b))
      : sel.batches.includes("-");
  } else if (classSel) {
    batchClassOk = classEntries.some((c) => sel.classes.includes(c.name));
  } else {
    batchClassOk = true;
  }

  const midBatchOk = !sel.midBatch || !!getMidBatchInfo(item.classList, item.program, currentBatch);
  const cutiOk = !sel.cuti || isOnLeave(item.onLeave, currentBatch, currentClass);
  const specialGuestOk = !sel.specialGuest || !!item.isSpecialGuest;
  const earlyRegisOk = !sel.earlyRegis || !!item.earlyRegistration;
  const ceaToChurchOk = !sel.ceaToChurch || !!item.ceaToChurch;
  return (
    batchClassOk && midBatchOk && cutiOk && specialGuestOk && earlyRegisOk && ceaToChurchOk &&
    inGroup(sel.roles, item.role || "-") &&
    inGroup(sel.programs, item.program) &&
    inGroup(sel.classTypes, item.classType || "-") &&
    inGroup(sel.scholarships, item.scholarshipType || "none") &&
    inGroup(sel.paymentPlans, item.paymentPlan || "full")
  );
};

// Tunggakan — sama dengan backend/utils/arrears.js: student "menunggak" untuk
// sebuah entry kelas kalau masih ada kelas billable SEBELUMNYA (batch naik,
// lalu CLASS_ORDER) yang belum lunas. Riwayat lama (batch < batchJoined) diabaikan.
const BILLABLE_CLASSES = ["OT", "NT", "BD", "BLC", "BCMF", "HH", "CBH", "CBD", "BWG", "MMM"];
const chronoKey = (c) => (parseInt(c.batch) || 0) * 100 + CLASS_ORDER.indexOf(c.name);
const hasArrearsBefore = (entries, target, batchJoined) => {
  const joined = parseInt(batchJoined) || 0;
  const targetKey = chronoKey(target);
  return entries.some(
    (c) =>
      c !== target &&
      BILLABLE_CLASSES.includes(c.name) &&
      c.status === "requested" &&
      (parseInt(c.batch) || 0) >= joined &&
      chronoKey(c) < targetKey,
  );
};

// Export dengan filter kelas (mis. BD batch 5): student yang masih menunggak
// kelas sebelumnya (mis. NT batch 5 belum lunas) TIDAK ikut di file. Tanpa
// filter kelas → tidak ada yang dikeluarkan.
const isExcludedForArrears = (item, sel) => {
  if (sel.classes.length === 0) return false;
  let entries = [];
  try {
    const cl = typeof item.classList === "string" ? JSON.parse(item.classList) : item.classList;
    entries = cl?.className || [];
  } catch { return false; }
  const targets = entries.filter(
    (c) =>
      sel.classes.includes(c.name) &&
      (sel.batches.length === 0 || (c.batch ? sel.batches.includes(c.batch) : sel.batches.includes("-"))),
  );
  if (targets.length === 0) return false;
  // Tetap ikut kalau minimal satu kelas yang difilter tidak punya tunggakan.
  return targets.every((t) => hasArrearsBefore(entries, t, item.batchJoined));
};

const StudentDataPage = ({ setLocationAdmin }) => {
  const address = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [studentData, setStudentData] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [errorMe, setErrorMe] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filtered, setFiltered] = useState([]);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterSel, setFilterSel] = useState(INITIAL_FILTER);
  const [draftFilter, setDraftFilter] = useState(INITIAL_FILTER);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [currentBatch, setCurrentBatch] = useState("");
  const [nextBatch, setNextBatch] = useState("");
  const [currentClass, setCurrentClass] = useState("");
  const [leaveModalStudent, setLeaveModalStudent] = useState(null);
  const [leaveFromClass, setLeaveFromClass] = useState("");
  const [leaveToClass, setLeaveToClass] = useState("");
  const [leaveFromBatch, setLeaveFromBatch] = useState("");
  const [leaveToBatch, setLeaveToBatch] = useState("");
  const [leaveSaving, setLeaveSaving] = useState(false);

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch (error) {
      setErrorMe(true);
    }
  };

  const fetchCurrentBatch = async () => {
    try {
      const res = await axios.get("/config/simulatedbatch");
      if (res.data.simulatedBatch) {
        const cb = res.data.simulatedBatch;
        setCurrentBatch(cb);
        setNextBatch(String(parseInt(cb) + 1));
        return;
      }
    } catch {}
    const now = new Date();
    const cb = String(now.getMonth() >= 8 ? now.getFullYear() - 2021 : now.getFullYear() - 2022);
    setCurrentBatch(cb);
    setNextBatch(String(parseInt(cb) + 1));
  };

  const fetchCurrentClass = async () => {
    try {
      const res = await axios.get("/config/currentclass");
      if (res.data.currentClass) setCurrentClass(res.data.currentClass);
    } catch {}
  };

  useEffect(() => {
    setLocationAdmin(address);
    getMe();
    fetchCurrentBatch();
    fetchCurrentClass();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get("/studentexport");
      setStudentData(response.data);
      setFilterOptions(deriveFilterOptions(response.data));
    } catch (error) {
      setErrorMe(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const result = studentData
        .filter((item) => matchesFilter(item, filterSel, currentBatch, currentClass))
        .filter((item) => {
          const n = search.toLowerCase().replace(/\s+/g, "");
          if (!n) return true;
          return (
            item.name.toLowerCase().replace(/\s+/g, "").includes(n) ||
            item.email.toLowerCase().replace(/\s+/g, "").includes(n) ||
            item.studentId.toLowerCase().replace(/\s+/g, "").includes(n)
          );
        })
        .sort((a, b) => {
          let result = 0;
          if (sortBy === "name") {
            result = a.name.localeCompare(b.name, "en", { sensitivity: "base" });
          } else if (sortBy === "studentId") {
            result = compareStudentId(a.studentId, b.studentId);
          }
          return sortOrder === "asc" ? result : -result;
        });
      setFiltered(result);
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [studentData, search, sortBy, sortOrder, filterSel, currentBatch, currentClass]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleNavigateEdit = (id) => {
    navigate(`/studentdatapage/${id}`);
  };

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        showCancelButton: false,
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe]);

  // Memakai item.id (primary key), bukan email: email tidak unik, jadi
  // menghapus lewat email bisa ikut menghapus student lain.
  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: "Delete Confirmation",
      text: `Are you sure you want to permanently delete ${item.name} (${item.studentId})?`,
      icon: "warning",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    });
    if (result.isConfirmed) {
      await axios.delete(`/student/${item.id}`);
      await Swal.fire({
        text: "Data Deleted!",
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
      fetchData();
    }
  };

  const openLeaveModal = (item) => {
    const existing =
      typeof item.onLeave === "string" ? JSON.parse(item.onLeave || "null") : item.onLeave;
    setLeaveFromClass(existing?.fromClass || "");
    setLeaveToClass(existing?.toClass || "");
    setLeaveFromBatch(existing?.fromBatch || "");
    setLeaveToBatch(existing?.toBatch || "");
    setLeaveModalStudent(item);
  };

  const closeLeaveModal = () => {
    setLeaveModalStudent(null);
    setLeaveFromClass("");
    setLeaveToClass("");
    setLeaveFromBatch("");
    setLeaveToBatch("");
  };

  const submitLeave = async (onLeavePayload) => {
    setLeaveSaving(true);
    try {
      await axios.patch(`/student/${leaveModalStudent.id}/leave`, { onLeave: onLeavePayload });
      await Swal.fire({
        text: onLeavePayload ? "Leave schedule saved!" : "Leave schedule cleared!",
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
      closeLeaveModal();
      fetchData();
    } catch (error) {
      Swal.fire({
        text: error.response?.data?.msg || "Failed to save leave schedule.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    } finally {
      setLeaveSaving(false);
    }
  };

  const handleSaveLeave = () => {
    const filledCount = [leaveFromClass, leaveToClass, leaveFromBatch, leaveToBatch].filter(
      (v) => v !== "",
    ).length;
    if (filledCount === 0) {
      Swal.fire({ text: "Fill in all 4 fields, or use Clear Leave to remove an existing leave.", icon: "error", background: "white", color: "black", confirmButtonColor: "#0004ff", confirmButtonText: "OK" });
      return;
    }
    if (filledCount < 4) {
      Swal.fire({ text: "Leave schedule is incomplete — fill in From Class, From Batch, To Class, and To Batch.", icon: "error", background: "white", color: "black", confirmButtonColor: "#0004ff", confirmButtonText: "OK" });
      return;
    }
    submitLeave({ fromClass: leaveFromClass, toClass: leaveToClass, fromBatch: leaveFromBatch, toBatch: leaveToBatch });
  };

  const handleClearLeave = async () => {
    const result = await Swal.fire({
      text: "Remove this student's leave schedule?",
      icon: "warning",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    });
    if (result.isConfirmed) submitLeave(null);
  };

  const openFilterModal = () => {
    setDraftFilter(filterSel);
    setFilterModalOpen(true);
  };

  const applyFilter = () => {
    setFilterSel(draftFilter);
    setFilterModalOpen(false);
  };

  const resetDraft = () => setDraftFilter(INITIAL_FILTER);

  const resetFilter = () => setFilterSel(INITIAL_FILTER);

  const toggleDraftSel = (group, value) => {
    setDraftFilter((prev) => ({
      ...prev,
      [group]: prev[group].includes(value)
        ? prev[group].filter((v) => v !== value)
        : [...prev[group], value],
    }));
  };

  const toggleDraftGroupAll = (group, options) => {
    setDraftFilter((prev) => ({
      ...prev,
      [group]: prev[group].length === options.length ? [] : options.map((o) => o.value),
    }));
  };

  const activeFilterCount =
    FILTER_GROUPS.filter(({ key }) => filterSel[key]?.length > 0).length +
    (filterSel.midBatch ? 1 : 0) +
    (filterSel.cuti ? 1 : 0) +
    (filterSel.specialGuest ? 1 : 0) +
    (filterSel.earlyRegis ? 1 : 0) +
    (filterSel.ceaToChurch ? 1 : 0);

  const formatClassListText = (classList) => {
    try {
      const cl = typeof classList === "string" ? JSON.parse(classList) : classList;
      return (cl?.className || [])
        .slice()
        .sort((a, b) => CLASS_ORDER.indexOf(a.name) - CLASS_ORDER.indexOf(b.name))
        .map((c) => `${c.name} (batch ${c.batch || "-"}, ${c.status})`)
        .join("; ");
    } catch {
      return "-";
    }
  };

  const formatOnLeaveText = (onLeave) => {
    try {
      const leave = typeof onLeave === "string" ? JSON.parse(onLeave || "null") : onLeave;
      if (!leave) return "-";
      return `${leave.fromClass} (batch ${leave.fromBatch}) - ${leave.toClass} (batch ${leave.toBatch})`;
    } catch {
      return "-";
    }
  };

  const handleExport = () => {
    if (filtered.length === 0) {
      Swal.fire({ text: "No data matches the current filter.", icon: "warning", background: "white", color: "black", confirmButtonColor: "#28a745" });
      return;
    }
    const exportable = filtered.filter((item) => !isExcludedForArrears(item, filterSel));
    const excludedCount = filtered.length - exportable.length;
    if (exportable.length === 0) {
      Swal.fire({ text: `All ${filtered.length} student(s) in this filter still have unpaid earlier classes, so nothing was exported.`, icon: "warning", background: "white", color: "black", confirmButtonColor: "#28a745" });
      return;
    }
    try {
      const rows = exportable.map((item) => {
        const midBatchInfo = getMidBatchInfo(item.classList, item.program, currentBatch);
        const midBatchText = midBatchInfo
          ? midBatchInfo.map((m) => `Batch ${m.batch}: ${m.first} - ${m.last}`).join(" | ")
          : "-";
        return {
          "Student ID": item.studentId,
          Name: item.name,
          Email: item.email,
          "Phone Number": item.phoneNumber,
          "Date of Birth": formatDmy(item.dateOfBirth),
          Address: item.address,
          City: item.city,
          Province: item.province,
          "Postal Code": item.postalCode,
          "Student Package": item.studentPackage,
          "Local Church": item.localChurch,
          "Last DJ": item.lastDj,
          Role: item.role,
          Scholarship: item.scholarshipType ? scholarshipLabel[item.scholarshipType] || item.scholarshipType : "-",
          "Class Type": item.classType,
          Program: item.program,
          "Payment Plan": item.paymentPlan === "installment" ? "Installment" : "Full Payment",
          "Tshirt Size": item.tshirtSize,
          "Class List": formatClassListText(item.classList),
          Batch: getClassBatches(item.classList).join(", "),
          "Mid-Batch Schedule": midBatchText,
          "Joined CEA Before": item.joinedCEA,
          "Year Joined": item.yearJoined,
          "Batch Joined": item.batchJoined,
          "Question 1": item.question1,
          "Question 2": item.question2,
          "Question 3": item.question3,
          "Question 4": item.question4,
          "Question 5": item.question5,
          "On Leave": formatOnLeaveText(item.onLeave),
          "Currently On Leave": isOnLeave(item.onLeave, currentBatch, currentClass) ? "Yes" : "No",
          "Early Regis": item.earlyRegistration ? "Yes" : "No",
          "CEA To Church": item.ceaToChurch ? item.ceaToChurch.churchName || "Yes" : "No",
          "Total Discount (%)": item.paymentSplit ? item.paymentSplit.totalDiscount : 0,
          "Discount Breakdown": item.paymentSplit?.totalDiscount > 0 ? discountBreakdownText(item.paymentSplit) : "-",
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
      const dateStamp = fileDateStamp();
      XLSX.writeFile(workbook, `student-data-${dateStamp}.xlsx`);
      if (excludedCount > 0) {
        Swal.fire({ text: `Exported ${exportable.length} student(s). ${excludedCount} student(s) were left out because they still have unpaid earlier classes.`, icon: "info", background: "white", color: "black", confirmButtonColor: "#28a745" });
      }
    } catch (error) {
      Swal.fire({ text: "Failed to export data.", icon: "error", background: "white", color: "black", confirmButtonColor: "#28a745" });
    }
  };

  if (isLoading) {
    return (
      <SyncLoader
        color={"#fb3104"}
        size={10}
        style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
      />
    );
  }

  // ── Filter modal (portal ke document.body supaya overlay 1 screen penuh) ──
  const draftPreviewCount = studentData.filter((item) =>
    matchesFilter(item, draftFilter, currentBatch, currentClass),
  ).length;

  const filterModal = filterModalOpen
    ? createPortal(
        <div className="adm-modal-backdrop" style={{ zIndex: 9999 }} onClick={() => setFilterModalOpen(false)}>
          <div className="adm-modal" style={{ width: "620px" }} onClick={(e) => e.stopPropagation()}>
            <div className="adm-modal-header">
              <div>
                <h5 className="adm-card-title" style={{ fontSize: "17px" }}>Filter Students</h5>
                <p className="adm-card-subtitle">
                  Kategori yang dikosongkan = semua nilai diikutkan. Klik Apply untuk menerapkan filter.
                </p>
              </div>
              <button type="button" className="adm-modal-close" aria-label="Close" onClick={() => setFilterModalOpen(false)}>
                ×
              </button>
            </div>

            <div className="adm-modal-body">
              <p className="adm-form-section-title">Quick filters</p>
              <div className="adm-chips" style={{ marginBottom: "20px" }}>
                {[
                  { key: "midBatch", label: "Mid-Batch Join" },
                  { key: "cuti", label: "On Leave (Cuti)" },
                  { key: "specialGuest", label: "Special Guest" },
                  { key: "earlyRegis", label: "Early Regis" },
                  { key: "ceaToChurch", label: "CEA To Church" },
                ].map(({ key, label }) => (
                  <label key={key} className={`adm-chip${draftFilter[key] ? " adm-chip--on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={draftFilter[key]}
                      onChange={() => setDraftFilter((prev) => ({ ...prev, [key]: !prev[key] }))}
                    />
                    {label}
                  </label>
                ))}
              </div>

              {filterOptions &&
                FILTER_GROUPS.map(({ key, title }) => (
                  <div key={key} style={{ marginBottom: "18px" }}>
                    <div className="adm-filter-group-head">
                      <span className="adm-form-section-title" style={{ margin: 0 }}>{title}</span>
                      <label className="adm-filter-select-all">
                        <input
                          type="checkbox"
                          checked={draftFilter[key].length === filterOptions[key].length && filterOptions[key].length > 0}
                          ref={(el) => {
                            if (el) el.indeterminate = draftFilter[key].length > 0 && draftFilter[key].length < filterOptions[key].length;
                          }}
                          onChange={() => toggleDraftGroupAll(key, filterOptions[key])}
                        />
                        Select all
                      </label>
                    </div>
                    <div className="adm-chips">
                      {filterOptions[key].map((opt) => {
                        const checked = draftFilter[key].includes(opt.value);
                        return (
                          <label key={opt.value} className={`adm-chip${checked ? " adm-chip--on" : ""}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleDraftSel(key, opt.value)} />
                            {opt.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>

            <div className="adm-modal-footer" style={{ justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "13px", color: "var(--adm-muted)" }}>
                <strong style={{ color: "var(--adm-text)" }}>{draftPreviewCount}</strong> / {studentData.length} students will match
              </span>
              <div className="d-flex gap-2">
                <button type="button" className="adm-btn adm-btn--secondary" onClick={resetDraft}>
                  Reset
                </button>
                <button type="button" className="adm-btn adm-btn--primary" onClick={applyFilter}>
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  // ── Leave modal (portal ke document.body) ──
  const leaveModal = leaveModalStudent
    ? createPortal(
        <div className="adm-modal-backdrop" style={{ zIndex: 9999 }}>
          <div className="adm-modal" style={{ width: "520px" }}>
            <div className="adm-modal-header">
              <div>
                <h5 className="adm-card-title" style={{ fontSize: "17px" }}>Set Leave (Cuti)</h5>
                <p className="adm-card-subtitle">
                  {leaveModalStudent.name} · {leaveModalStudent.studentId}
                </p>
              </div>
              <button type="button" className="adm-modal-close" aria-label="Close" onClick={closeLeaveModal}>
                ×
              </button>
            </div>

            <div className="adm-modal-body">
              <p className="adm-form-section-title">Current class schedule</p>
              <div className="adm-schedule-box">
                {getClassScheduleList(leaveModalStudent.classList).map((c) => (
                  <div key={c.name} className="adm-schedule-row">
                    <span>{c.name}</span>
                    <span className="adm-schedule-batch">{c.batch ? `Batch ${c.batch}` : "-"}</span>
                  </div>
                ))}
              </div>

              <p className="adm-form-section-title" style={{ marginTop: "18px" }}>Leave schedule</p>
              <div className="adm-form-grid">
                <div>
                  <label className="form-label">From Class</label>
                  <select className="form-select" value={leaveFromClass} onChange={(e) => setLeaveFromClass(e.target.value)}>
                    <option value="">-</option>
                    {CLASS_ORDER.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">From Batch</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 4"
                    value={leaveFromBatch}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setLeaveFromBatch(val);
                      setLeaveToBatch((prev) => (prev === "" ? val : prev));
                    }}
                  />
                </div>
                <div>
                  <label className="form-label">To Class</label>
                  <select className="form-select" value={leaveToClass} onChange={(e) => setLeaveToClass(e.target.value)}>
                    <option value="">-</option>
                    {CLASS_ORDER.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">To Batch</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 4"
                    value={leaveToBatch}
                    onChange={(e) => setLeaveToBatch(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
              </div>
              <p className="adm-help" style={{ marginTop: "10px" }}>
                Jika cuti menyeberang batas batch (mis. MMM batch 4 sampai MASTERCLASS batch 5),
                isi "To Batch" dengan batch selanjutnya. Jika masih dalam satu batch, isi "From Batch"
                dan "To Batch" dengan angka yang sama.
              </p>
            </div>

            <div className="adm-modal-footer" style={{ justifyContent: "space-between" }}>
              <button type="button" className="adm-btn adm-btn--danger" onClick={handleClearLeave} disabled={leaveSaving}>
                Clear Leave
              </button>
              <div className="d-flex gap-2">
                <button type="button" className="adm-btn adm-btn--secondary" onClick={closeLeaveModal} disabled={leaveSaving}>
                  Cancel
                </button>
                <button type="button" className="adm-btn adm-btn--primary" onClick={handleSaveLeave} disabled={leaveSaving}>
                  {leaveSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  const sortIcon = (key) =>
    sortBy === key ? (
      <i className={`bi ${sortOrder === "asc" ? "bi-arrow-up" : "bi-arrow-down"}`} style={{ marginLeft: "4px" }}></i>
    ) : (
      <i className="bi bi-arrow-down-up" style={{ marginLeft: "4px", opacity: 0.35 }}></i>
    );

  return (
    <div className="adm-main">
      <PageHeader
        title="Student Data"
        subtitle="All official students. Use Filter to narrow the list, then export it to Excel."
        actions={
          <>
            <button type="button" className="adm-btn adm-btn--secondary" onClick={handleExport}>
              <i className="bi bi-file-earmark-excel" style={{ color: "#16a34a" }}></i>
              Export to Excel
            </button>
            {/* Add Student — jalur admin untuk student yang datanya masuk di luar
                website. Perlu diingat ini MELEWATI pendaftaran & pembayaran;
                student hasil pendaftaran normal tetap lewat halaman Approval. */}
            <button type="button" className="adm-btn adm-btn--primary" onClick={() => navigate("/addstudent")}>
              <i className="bi bi-person-plus"></i>
              Add Student
            </button>
          </>
        }
      />

      <div className="adm-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, or student ID" />
        <button
          type="button"
          className={`adm-btn ${activeFilterCount > 0 ? "adm-btn--dark" : "adm-btn--secondary"}`}
          onClick={openFilterModal}
        >
          <i className="bi bi-funnel"></i>
          Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </button>
        {activeFilterCount > 0 && (
          <button type="button" className="adm-btn adm-btn--ghost" onClick={resetFilter}>
            Reset filter
          </button>
        )}
      </div>

      <div className="adm-card adm-card--flush">
        {filtered.length === 0 ? (
          <EmptyState
            icon="bi-people"
            title="No students found"
            text={search || activeFilterCount > 0 ? "Try a different search or reset the filter." : "There are no students yet."}
          />
        ) : (
          <div className="adm-table-wrap adm-table-wrap--scroll">
            <table className="adm-table" style={{ minWidth: "820px" }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort("studentId")} style={{ cursor: "pointer", width: "140px" }}>
                    Student ID {sortIcon("studentId")}
                  </th>
                  <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                    Name {sortIcon("name")}
                  </th>
                  <th>Program</th>
                  <th>Mid-Batch Schedule</th>
                  <th className="adm-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, index) => {
                  const midBatchInfo = getMidBatchInfo(item.classList, item.program, currentBatch);
                  return (
                    // id is the unique row key; email and studentId aren't unique in the data.
                    <tr key={item.id ?? `${item.studentId}-${item.email}-${index}`}>
                      <td style={{ color: "var(--adm-text-2)" }}>{item.studentId}</td>
                      <td>
                        <div className="adm-cell-title">{item.name}</div>
                        <div className="adm-badges">
                          {isOnLeave(item.onLeave, currentBatch, currentClass) && (
                            <span className="adm-badge adm-badge--warning">Cuti</span>
                          )}
                          {item.scholarshipType && (
                            <span className="adm-badge adm-badge--purple">
                              {scholarshipLabel[item.scholarshipType] || item.scholarshipType}
                            </span>
                          )}
                          {item.ceaToChurch && (
                            <span
                              className="adm-badge adm-badge--primary"
                              title={`Registered through CEA To Church${item.ceaToChurch.churchName ? ` · ${item.ceaToChurch.churchName}` : ""}`}
                            >
                              CEA To Church
                            </span>
                          )}
                          {item.paymentSplit?.totalDiscount > 0 && (
                            <span
                              className="adm-badge adm-badge--success"
                              title={discountBreakdownText(item.paymentSplit)}
                              style={{ cursor: "help" }}
                            >
                              Discount {item.paymentSplit.totalDiscount}%
                            </span>
                          )}
                          {item.isSpecialGuest && <span className="adm-badge adm-badge--purple">Special Guest</span>}
                          {item.earlyRegistration && <span className="adm-badge adm-badge--info">Early Regis</span>}
                        </div>
                      </td>
                      <td>{item.program}</td>
                      <td>
                        {midBatchInfo ? (
                          midBatchInfo.map(({ batch, first, last }) => (
                            <div key={batch} style={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                              <span style={{ fontWeight: 700, color: "var(--adm-info)" }}>Batch {batch}</span> · {first} → {last}
                            </div>
                          ))
                        ) : (
                          <span style={{ color: "var(--adm-muted)" }}>-</span>
                        )}
                      </td>
                      <td className="adm-col-actions">
                        <div className="adm-actions">
                          <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => handleNavigateEdit(item.id)}>
                            <i className="bi bi-pencil"></i>
                            Edit
                          </button>
                          <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => openLeaveModal(item)}>
                            <i className="bi bi-calendar2-week"></i>
                            Set Cuti
                          </button>
                          <button
                            type="button"
                            className="adm-icon-btn adm-icon-btn--danger"
                            title="Delete student"
                            aria-label="Delete student"
                            onClick={() => handleDelete(item)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="adm-table-footer">
          <span>
            Showing <strong style={{ color: "var(--adm-text)" }}>{filtered.length}</strong> of {studentData.length} students
          </span>
        </div>
      </div>

      {filterModal}
      {leaveModal}
    </div>
  );
};

export default StudentDataPage;
